import * as React from 'react';
import { useMsal } from "@azure/msal-react";
import { AuthenticationResult, AuthError } from "@azure/msal-browser";
import { SharePointLoginProps } from '../types';
import { AuthService } from '../services/authService';
import { loginRequest } from '../authConfig';

const SharePointLogin: React.FC<SharePointLoginProps> = ({ onComplete }) => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [detailedError, setDetailedError] = React.useState<string | null>(null);
  
  const authService = React.useMemo(() => new AuthService(instance), [instance]);

  const handleMicrosoftLogin = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setDetailedError(null);
    
    try {
      console.log("Attempting login with MSAL...");
      try {
        const loginResponse: AuthenticationResult = await instance.loginPopup(loginRequest);
        
        if (loginResponse.account) {
          //console.log("Login successful", loginResponse.account.username);
          
          try {
            const accessToken = await authService.getAccessToken();
            //console.log("Access token acquired successfully");
            
            if (loginResponse.account) {
              const userInfo = {
                username: loginResponse.account.username,
                name: loginResponse.account.name,
                homeAccountId: loginResponse.account.homeAccountId,
                environment: loginResponse.account.environment,
                tenantId: loginResponse.account.tenantId,
                localAccountId: loginResponse.account.localAccountId,
              };
              if (loginResponse.idTokenClaims) {
                const claims = loginResponse.idTokenClaims as any;
                const userClaims = {
                  email: claims.email || claims.preferred_username || '',
                  name: claims.name || '',
                  givenName: claims.given_name || '',
                  familyName: claims.family_name || '',
                  jobTitle: claims.jobTitle || claims.title || '',
                  department: claims.department || '',
                  objectId: claims.oid || '',
                  tenantId: claims.tid || '',
                  roles: claims.roles || [],
                  groups: claims.groups || [],
                  ipAddress: claims.ipaddr || '',
                  loginHint: claims.login_hint || '',
                };
                Object.assign(userInfo, userClaims);
              }
              localStorage.setItem('userInfo', JSON.stringify(userInfo));
              localStorage.setItem('msalAccessToken', accessToken);
              localStorage.setItem('loginTimestamp', new Date().toISOString());
              
              console.log("User information stored:", userInfo);
            }
            
            onComplete();
          } catch (tokenError) {
            console.error("Token acquisition failed", tokenError);
            const tokenErrorDetails = tokenError instanceof Error ? tokenError.message : String(tokenError);
            setDetailedError(`Failed to acquire token: ${tokenErrorDetails}`);
            setError("Failed to acquire necessary permissions. Please try again.");
          }
        }
      } catch (loginError) {
        console.error("Login popup failed - detailed error:", loginError);
        
        if (loginError instanceof AuthError) {
          console.error("Error code:", loginError.errorCode);
          console.error("Error message:", loginError.errorMessage);
          setDetailedError(`Error code: ${loginError.errorCode}, Message: ${loginError.errorMessage}`);
          
          if (loginError.errorCode === "user_cancelled") {
            setError("Login was cancelled. Please try again.");
          } else if (loginError.errorCode === "consent_required") {
            setError("Consent required. Please accept the permissions requested.");
          } else {
            setError(`Login failed: ${loginError.errorMessage}`);
          }
        } else {
          const errorDetails = loginError instanceof Error ? loginError.message : String(loginError);
          setDetailedError(errorDetails);
          setError("Login failed. Please try again.");
        }
      }
    } catch (err) {
      console.error("Unexpected error during login process:", err);
      const errorDetails = err instanceof Error ? err.message : String(err);
      setDetailedError(errorDetails);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="step-content">
      <h2>Welcome to KCNSC O365 External Access</h2>
      <div><img src="https://kcnsc.doe.gov/media/ta4mshlj/kcnsc-by-honeywell-light-vert-c.png" alt="" width={250} /></div>
      <p>Please log in with your Microsoft account to access KCNSC resources</p>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {detailedError && (
        <div className="detailed-error">
          <details>
            <summary>Technical Details (for troubleshooting)</summary>
            <pre>{detailedError}</pre>
          </details>
        </div>
      )}
      
      <button 
        className="primary-button"
        onClick={handleMicrosoftLogin}
        disabled={isLoading}
      >
        {isLoading ? 'Logging in...' : 'Login with Microsoft'}
      </button>
      
      <div className="troubleshooting-tips">
        <p>Having trouble? Try these steps:</p>
        <ol>
          <li>Ensure you're using a valid account for this tenant</li>
          <li>Check if the account has the necessary permissions</li>
          <li>Try clearing your browser cache</li>
          <li>View browser console (F12) for detailed error logs</li>
        </ol>
      </div>
    </div>
  );
};

export default SharePointLogin;
